import {Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ButtonComponent} from "../../../shared-module/components/button/button.component";
import {DatePipe} from "@angular/common";
import {AuthService} from "../../../shared-module/services/auth.service";
import Relation from "../../../shared-module/model/relation.model";
import Subscription from "../../../shared-module/model/subscription.model";
import {ProductType} from "../../../shared-module/types/product.type";
import {ProductState} from "../../../shared-module/enums/product-state";
import Product from "../../../shared-module/model/product.model";
import {ContractInformationComponent} from "../../components/contract-information/contract-information.component";
import {
  BadgeWithContentComponent
} from "../../../shared-module/components/badge-with-content/badge-with-content.component";
import {CurrencyComponent} from "../../../shared-module/components/currency/currency.component";
import {DividerComponent} from "../../../shared-module/components/divider/divider.component";
import DiscountLine from "../../../shared-module/interfaces/DiscountLine.interface";
import {PhoneDetailsComponent} from "../../components/phone-details/phone-details.component";
import {TelevisionDetailsComponent} from "../../components/television-details/television-details.component";
import {Observable} from "rxjs";
import {ModalEventType} from "../../../shared-module/types/modal.type";
import ModalService from "../../../shared-module/services/modal.service";
import ProductStore from "../../../shared-module/services/Stores/product.store";
import {
  ProductTerminateModalComponent
} from "../../components/product-terminate-modal/product-terminate-modal.component";
import {ModalEventTypes} from "../../../shared-module/enums/modal.event";
import {IconComponent} from "../../../shared-module/components/icon/icon.component";
import {CardComponent} from "../../../shared-module/components/card/card.component";
import {
  SubscriptionSelectorComponent
} from "../../../shared-module/components/subscription-selector/subscription-selector.component";
import {NotFoundComponent} from "../../../shared-module/components/not-found/not-found.component";

@Component({
    selector: 'app-subscription',
    imports: [
    ButtonComponent,
    ContractInformationComponent,
    BadgeWithContentComponent,
    CurrencyComponent,
    DividerComponent,
    DatePipe,
    IconComponent,
    CardComponent,
    SubscriptionSelectorComponent,
    NotFoundComponent
],
    templateUrl: './subscription.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './subscription.component.scss'
})

export class SubscriptionComponent implements OnInit {
  private readonly authService: AuthService = inject(AuthService);
  private readonly modalService: ModalService = inject(ModalService);
  private readonly productStore: ProductStore = inject(ProductStore);

  public typeToIconAndTitle:
    Record<ProductType, { icon: string, title: string, path: string }> = {
    [ProductType.TELEVISION]: {
      icon: 'television',
      title: 'Televisie',
      path: 'television',
    },
    [ProductType.INTERNET]: {
      icon: 'wifi',
      title: 'Internet',
      path: 'internet',
    },
    [ProductType.PHONE]: {
      icon: 'phone',
      title: 'Bellen',
      path: 'phone',
    },
    [ProductType.DNS_FILTER]: {
      icon: 'shield-check',
      title: 'Dns Filter',
      path: 'dns_filter',
    }
  }
  protected readonly ProductType: typeof ProductType = ProductType;
  protected readonly ProductState: typeof ProductState = ProductState;
  protected selectedSubscription: Subscription | null = null;
  protected activeRelation: Relation | null = null;
  protected subscriptionCount: number = 0;
  private totalPrice: number[] = [];
  private modalDetailsSubscription: Observable<ModalEventType> | null = null;
  private modalTerminateSubscription: Observable<ModalEventType> | null = null;

  public ngOnInit(): void {
    this.authService.getUser(['relations.subscriptions.products.discount_lines']).subscribe();

    this.authService.getSelectedSubscription().subscribe((subscription: Subscription | null) => {
      if (!subscription?.products) {
        return;
      }
      this.selectedSubscription = subscription;
      this.productStore.reset();
      this.productStore.forParent('subscriptions', this.selectedSubscription);
      this.calculateTotalPrice();
    });

    this.authService.getActiveRelation().subscribe((relation: Relation | null) => {
      if (!relation || !relation.subscriptions) {
        return;
      }
      this.activeRelation = relation;
      this.subscriptionCount = relation.subscriptions ? relation.subscriptions.length : 0;
      this.calculateTotalPrice();
    });
  }

  public selectedSubscriptionChanged(event: unknown): void {
    const subscription: Subscription = event as Subscription;
    this.selectedSubscription = subscription;
    this.authService.setSelectedSubscription(subscription);
    this.ngOnInit();
  }

  public getProductTypes(): ProductType[] {
    return Object.values(ProductType).map((type: string) => {
      return type as ProductType
    });
  }

  public getProductForTypes(productType: ProductType): Product[] {
    if (!this.selectedSubscription) {
      return [];
    }
    return this.selectedSubscription.products.filter((product: Product) => {
      return product.product_type === productType;
    }).filter((product: Product) => {
      return product.state === ProductState.ACTIVE;
    });
  }

  public getSelectedTotalPrice(): number {
    if (this.selectedSubscription) {
      return this.totalPrice[this.selectedSubscription.id];
    }
    return 0;
  }

  public calculateTotalPrice() {
    if (!this.selectedSubscription || !this.activeRelation) {
      return;
    }
    const isBusiness: boolean = this.activeRelation.is_business;
    const subscrId: number = this.selectedSubscription.id;
    this.totalPrice[subscrId] = 0;
    for (const productType of this.getProductTypes()) {
      const products: Product[] = this.getProductForTypes(productType);
      products.forEach((product: Product) => {
        product.discount_lines?.forEach((discountLine: DiscountLine) => {
          this.totalPrice[subscrId] += isBusiness ? discountLine.amount_excl : discountLine.amount_incl;
        });
        this.totalPrice[subscrId] += product.original_price
      });
    }
  }

  public hasDetails(product: Product): boolean {
    return product?.specs?.show_details === true;
  }

  protected showTelevisionDetailsModal(product: Product): void {
    if (this.modalDetailsSubscription) {
      this.modalService.close();
    }

    this.modalDetailsSubscription = this.modalService.open(TelevisionDetailsComponent, product, {
      width: '600px',
      showCloseButton: true,
      showBackdrop: true,
      title: product.specs.title,
      badgeIcon: product.specs.icon
    });
  }

  protected showPhoneDetailsModal(product: Product): void {
    if (this.modalDetailsSubscription) {
      this.modalService.close();
    }

    this.modalDetailsSubscription = this.modalService.open(PhoneDetailsComponent, product, {
      title: product.specs.title,
      width: '800px',
      titleOffset: '0px',
      showCloseButton: true,
      showBackdrop: true,
      badgeIcon: product.specs.icon
    });
  }

  public terminate(product: Product): void {
    const modal: typeof ProductTerminateModalComponent = ProductTerminateModalComponent;

    if (this.modalDetailsSubscription) {
      this.modalService.close();
    }

    this.modalTerminateSubscription = this.modalService.open(modal, product, {
      width: '600px',
      showCloseButton: true,
      showBackdrop: true,
      title: product.specs.title,
      badgeIcon: product.specs.icon
    });

    this.modalTerminateSubscription.subscribe((event: ModalEventType) => {
      if (event.type === ModalEventTypes.Confirmed) {
        const updatedProduct: Product = event.value as Product;
        const index: number | undefined = this.selectedSubscription?.products?.findIndex((product: Product) => product.id === updatedProduct.id);
        if (index && this.selectedSubscription?.products) {
          this.selectedSubscription.products[index] = updatedProduct;
        }
      }
    });
  }
}
